/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  forwardRef,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import { FileTableProps } from '../FileTable';
import { DbShare } from '~src/types/db';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { SelectedShareKeysProps } from '~components/molecules/Tabs/TenderTabs/TenderTabs';
import { SimpleDeleteModal } from '~components/popups-modals/DeleteModal/SimpleDeleteModal';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { appLog } from '~src/utils/log';
import { Empty, Input, Modal, message } from 'antd';
import isEmail from 'validator/es/lib/isEmail';
import {
  claimSharedTender,
  getShareLogs,
  resendInviteShare,
  updateShare,
  updateSharedTenderState,
} from '~src/services/api/shares';
import {
  changeSecondaryShareKeyState,
  createSecondaryShareKeys,
} from '~src/services/api/secondaryVendors';
import { EditShareTenderModal } from '~components/popups-modals/EditShareTenderModal';
import { EditSecondaryShareKeyModal } from '~components/popups-modals/SecondaryShareKeyModals/EditSecondaryShareKeyModal';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { useColumns } from './useColumns';
import { styleFn } from './TenderSharesListTable.style';
import { EditInviteModal } from '~components/popups-modals/EditInvite.modal';
import { DetailsLogModal } from '~components/popups-modals/DetailsLogModal/DetailsLog.modal';
import {
  dbTenderShareFileToTableEntry,
  TableEntry,
} from '~components/atoms/FileTables/FileTable.utils';
import UploadPanel from '~components/atoms/UploadPanel/UploadPanel';
import UploadContext from '~src/contexts/UploadContext';
import { TableRowSelection } from 'antd/lib/table/interface';
import InviteModal from '~components/popups-modals/InviteModal/InviteModal.modal';
import { SecondaryShareKeysDrawer } from './SecondaryShareKeysDrawer/SecondaryShareKeysDrawer';
import { useAdminSecondaryShareKeys } from '~src/hooks/api-hooks/useAdminSecondaryShareKeys';

export enum ResourceState {
  NONE,
  DRAFT,
  PARTIAL,
  AVAILABLE,
  ACCEPTED,
  REJECTED,
  COMPLETED,
  PROCESSED,
  VALIDATED,
  REMOVED,
  DELETED,
  EXPIRED,
}

type TenderSharesListTableProps = Pick<
  FileTableProps,
  'highlightedRow' | 'focusOptions' | 'scroll'
> &
  SelectedShareKeysProps & {
    // Admin identity - optional because vendor mode (isOnMyTender) has no
    // numeric tenderId/projectId, only a GUID shareId/shareKey below.
    tenderId?: string;
    projectId?: number;
    // Vendor identity (My-Tenders -> Subcontractors tab) - the GUID
    // share_id/Share-Key header the secondaryVendors.ts mutations need.
    shareId?: string;
    shareKey?: string;
    // Vendor mode flag - see TenderSharesBlock.tsx's isOnMyTender. Branches
    // columns, disables admin-only interactions, and swaps in the Invite
    // button/modal instead.
    isOnMyTender?: boolean;
    // Primary-/Secondary Share Keys (vendor tab): the Invite trigger button
    // now lives in MyTendersTabs.tsx's header (mirrors the admin's own
    // Invite button living in TenderActions.tsx, not inside this table) -
    // this component only owns the Modal + mutation, controlled from above.
    isInviteModalOpen?: boolean;
    onCloseInviteModal?: () => void;
    shareKeysList: DbShare[];
    // Whether Secondary Share Keys are activated for this tender - gates
    // whether the "+" expand column exists on the table at all. Optional/
    // defaulted to false so nothing breaks for callers that don't pass it.
    isSecondaryKeysOn?: boolean;
    // Blocks inviting new subcontractors from the Secondary Share Keys
    // drawer once the tender is closed, matching the primary Vendors
    // "Invite" flow's same rule. Optional/defaulted to false so nothing
    // breaks for callers that don't pass it (e.g. the print-only instance).
    tenderCompleted?: boolean;
    isLoading: boolean;
    onSelect?: TableRowSelection<TableEntry>['onSelect'];
    onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
    setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
    selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
    bottomViewHeight?: number;
  } & (
    | {
        viewOnly: false;
        handleChangeShowRejected: (show: boolean) => void;
      }
    | {
        viewOnly: true;
        handleChangeShowRejected?: undefined;
      }
  );

export const TenderSharesListTable = forwardRef<
  HTMLDivElement,
  TenderSharesListTableProps
>(
  (
    {
      shareKeysList,
      isSecondaryKeysOn = false,
      tenderCompleted = false,
      isLoading,
      tenderId,
      projectId,
      shareId,
      shareKey,
      isOnMyTender = false,
      isInviteModalOpen = false,
      onCloseInviteModal,
      highlightedRow,
      focusOptions,
      selectedSharedKeys,
      viewOnly,
      handleChangeShowRejected,
      onSelect,
      onSelectAll,
      setSelectedItems,
      selectedKeys,
      bottomViewHeight = 0,
      ...tableProps
    },
    ref
  ) => {
    const styles = useStyles(styleFn);
    const intl = useIntl();

    const [shareToEdit, setShareToEdit] = useState<DbShare | null>(null);
    const [shareToReactivate, setShareToReactivate] = useState<DbShare | null>(
      null
    );
    const [shareToRemove, setShareToRemove] = useState<DbShare | null>(null);
    const [shareToReject, setShareToReject] = useState<DbShare | null>(null);
    const [shareToClaim, setShareToClaim] = useState<DbShare | null>(null);
    const [shareToEditInvite, setShareToEditInvite] = useState<any>(null);
    const [shareToDetailsLog, setShareToDetailLog] = useState<DbShare | null>(
      null
    );
    const [
      shareToResendInvite,
      setShareToResendInvite,
    ] = useState<DbShare | null>(null);

    // Primary-/Secondary Share Keys: the "+" icon opens this side Drawer
    // instead of expanding the row inline.
    const [
      secondaryDrawerRecord,
      setSecondaryDrawerRecord,
    ] = useState<DbShare | null>(null);

    // Primary-/Secondary Share Keys (Tender Admin Update): live fetch for
    // the clicked primary row's subcontractors, via normal admin auth (no
    // Share-Key header needed for this admin-scoped endpoint). Named
    // distinctly from the component's own `isLoading` prop above (main
    // table's loading spinner) to avoid shadowing it.
    const adminSecondaryShareKeysQuery = useAdminSecondaryShareKeys(
      {
        projectId,
        tenderId,
        shareKey: secondaryDrawerRecord?.share_key_id ?? '',
        states: [
          DB_STATE.AVAILABLE,
          DB_STATE.ACCEPTED,
          DB_STATE.REJECTED,
          DB_STATE.REMOVED,
        ],
      },
      {
        // Skip the network round-trip entirely once we already know (via
        // the flag on the clicked row) that there's nothing to fetch.
        enabled:
          !!secondaryDrawerRecord &&
          secondaryDrawerRecord.has_secondary_share_keys !== false,
      }
    );

    const [rightClickEntity, setRightClickEntity] = useState<{
      share?: DbShare;
    }>({});

    const tableContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const el = tableContainerRef.current;
      if (!el) return;
      const handler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.ant-table-row')) {
          e.stopPropagation();
          e.preventDefault();
        }
      };
      el.addEventListener('contextmenu', handler);
      return () => el.removeEventListener('contextmenu', handler);
    }, []);

    const [
      updateTenderShareStateMutation,
      { isLoading: isUpdateShareStateLoading },
    ] = useMutation(updateShare, {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'tender-share-update-failed' }));
        appLog('updateTenderShareStateMutation -> item', item);
        appLog('updateTenderShareStateMutation -> err', error);
      },
    });

    const [dbClaimSharedTender] = useMutation(claimSharedTender, {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      },
      onError: (err) => {
        message.error(intl.formatMessage({ id: 'tender-share-update-error' }));
      },
    });

    const [dbRejectSharedTender] = useMutation(updateSharedTenderState, {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      },
      onError: (err) => {
        message.error(intl.formatMessage({ id: 'tender-share-update-error' }));
      },
    });

    const [dbResendInviteSharedTender] = useMutation(resendInviteShare, {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      },
      onError: (err) => {
        message.error(intl.formatMessage({ id: 'tender-share-update-error' }));
      },
    });

    // Primary-/Secondary Share Keys (vendor tab): Remove/Reactivate for a
    // secondary share key, via the acting Primary-Share-Key's identity
    // (shareId/shareKey), not the admin's projectId/numeric tenderId.
    const [doChangeSecondaryShareKeyState] = useMutation(
      changeSecondaryShareKeyState,
      {
        onSuccess: () => {
          queryCache.invalidateQueries([QueryKeys.secondaryShareKeys]);
        },
        onError: (error, item) => {
          message.error(
            intl.formatMessage({ id: 'tender-share-update-failed' })
          );
          appLog('doChangeSecondaryShareKeyState -> item', item);
          appLog('doChangeSecondaryShareKeyState -> err', error);
        },
      }
    );

    const handleRemoveShare = async () => {
      try {
        if (!shareToRemove) return;
        if (isOnMyTender && shareId && shareKey) {
          await doChangeSecondaryShareKeyState({
            shareId,
            shareKey,
            secondaryShareKey: shareToRemove.share_key_id,
            action: 'remove',
          });
        } else if (projectId && tenderId) {
          await updateTenderShareStateMutation({
            projectId,
            tenderId: Number(tenderId),
            shareKey: shareToRemove.share_key_id,
            action: 'remove',
          });
        }
        setShareToRemove(null);
      } catch (err) {
        appLog('handleRemoveShare -> err', err);
      }
    };

    const handleReactivateShare = async () => {
      try {
        if (!shareToReactivate) return;
        if (isOnMyTender && shareId && shareKey) {
          await doChangeSecondaryShareKeyState({
            shareId,
            shareKey,
            secondaryShareKey: shareToReactivate.share_key_id,
            action: 'reactivate',
          });
        } else if (projectId && tenderId) {
          await updateTenderShareStateMutation({
            projectId,
            tenderId: Number(tenderId),
            shareKey: shareToReactivate.share_key_id,
            action: 'reactivate',
          });
        }
        setShareToReactivate(null);
      } catch (err) {
        appLog('handleReactivateShare -> err', err);
      }
    };

    const handleRejectShare = async () => {
      try {
        if (projectId && tenderId && shareToReject) {
          await dbRejectSharedTender({
            shareId: shareToReject.share_id,
            shareKey: shareToReject.share_key_id,
            action: 'reject',
          });
        }
      } catch (err) {
        appLog('handleRejectShare -> err', err);
      }
    };

    const handleClaimShare = async () => {
      try {
        if (projectId && tenderId && shareToClaim) {
          await dbClaimSharedTender({
            shareId: shareToClaim.share_id,
            shareKey: shareToClaim.share_key_id,
          });
        }
      } catch (err) {
        appLog('handleRejectShare -> err', err);
      }
    };

    const handleResendInvite = async () => {
      // console.log(`handleResendInvite worked`);
      try {
        if (projectId && tenderId && shareToResendInvite) {
          await dbResendInviteSharedTender({
            projectId,
            tenderId: Number(tenderId),
            shareKey: shareToResendInvite.share_key_id,
          });
          setShareToResendInvite(null);
        }
      } catch (err) {
        appLog('handleResendInvite -> err', err);
      }
    };

    const handleRightOverlayItemClick = (item: DataContextMenuItemType) => {
      if (item.modal === 'delete')
        setShareToRemove(rightClickEntity.share || null);
      else if (item.modal === 'edit')
        setShareToEdit(rightClickEntity.share || null);
      else if (item.modal === 'reactivate')
        setShareToReactivate(rightClickEntity.share || null);
      else if (item.modal === 'reject')
        setShareToReject(rightClickEntity.share || null);
      else if (item.modal === 'claim')
        setShareToClaim(rightClickEntity.share || null);
      else if (item.modal === 'details-log')
        setShareToDetailLog(rightClickEntity.share || null);
      else if (item.modal === 'edit-invite') {
        // Primary-/Secondary Share Keys (vendor tab): edit-invite is the
        // only "edit" affordance dataContextMenu.shareKey exposes, but a
        // vendor row must open our lightweight Edit modal, not the admin's
        // big resend-invite InviteModal.
        if (isOnMyTender) setShareToEdit(rightClickEntity.share || null);
        else setShareToEditInvite(rightClickEntity.share || null);
      } else if (item.modal === 'resend-invite')
        setShareToResendInvite(rightClickEntity.share || null);
    };

    const handleContextMenuEvent = (e: ReactMouseEvent<any>) => {
      e.preventDefault();
      const customEvent = new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 2,
        buttons: 2,
        clientX: e.clientX,
        clientY: e.clientY,
      });
      e.currentTarget.dispatchEvent(customEvent);
    };

    const handleSharesRow: TableProps<DbShare>['onRow'] = (data: DbShare) => {
      return {
        onContextMenu: (e) => {
          setRightClickEntity({ share: data });
          handleContextMenuEvent(e);
        },
      };
    };

    const columns = useColumns({
      styles,
      // Restored to mirror the admin's Shares tab 1:1 (ellipsis-menu +
      // Accepted columns included) - Contracts stays hidden for vendor rows
      // (secondary keys have no contract-offer data, per spec), but every
      // other column now matches admin regardless of isOnMyTender.
      viewOnly,
      hideContractsColumn: isOnMyTender,
      onMenuDetailsClick: (e, share) => {
        setRightClickEntity({ share });
        handleContextMenuEvent(e);
      },
    });

    // Primary-/Secondary Share Keys (vendor tab, Invite only - List/Invite
    // for this pass, Remove/Reactivate deferred): create a new subcontractor
    // under the acting Primary-Share-Key. The open/close boolean itself is
    // now controlled from MyTendersTabs.tsx's header button - only the form
    // fields + mutation stay local here.
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteTitle, setInviteTitle] = useState('');

    const resetInviteForm = () => {
      onCloseInviteModal?.();
      setInviteEmail('');
      setInviteTitle('');
    };

    const [doCreateSecondaryShareKey, { isLoading: isInviting }] = useMutation(
      createSecondaryShareKeys,
      {
        onSuccess: () => {
          queryCache.invalidateQueries([QueryKeys.secondaryShareKeys]);
          message.success(intl.formatMessage({ id: 'invite-share-success' }));
          resetInviteForm();
        },
        onError: (error) => {
          appLog('createSecondaryShareKeys -> err', error);
          message.error(intl.formatMessage({ id: 'invite-share-edit-failed' }));
        },
      }
    );

    const handleInviteSubmit = async () => {
      if (!shareId || !shareKey || !isEmail(inviteEmail)) return;
      try {
        await doCreateSecondaryShareKey({
          shareId,
          shareKey,
          body: [{ email: inviteEmail, title: inviteTitle }],
        });
      } catch (error) {
        appLog('handleInviteSubmit -> err', error);
      }
    };

    const editableShare = useMemo(
      () => shareKeysList.find((item) => item.id === shareToEdit?.id),
      [shareToEdit]
    );

    const overlayItems = useMemo(() => {
      const chosenShareState = rightClickEntity.share?.share_key_state;

      let items: DataContextMenuItemType[];
      if (chosenShareState === ResourceState.REMOVED) {
        items = dataContextMenu.shareKeyRemoved;
      } else if (chosenShareState === ResourceState.NONE) {
        items = dataContextMenu.shareKeyClaimed;
      } else {
        items = dataContextMenu.shareKey;
      }

      // Primary-/Secondary Share Keys (vendor tab): grey out every action
      // once the tender is closed - shallow-clone since dataContextMenu's
      // arrays are shared/static and must not be mutated in place.
      if (isOnMyTender && tenderCompleted) {
        return items.map((item) => ({
          ...item,
          disabled: true,
          disabledTooltip: 'tender-data-completed',
        }));
      }

      return items;
    }, [rightClickEntity, isOnMyTender, tenderCompleted]);

    let panelNode: React.ReactElement | undefined;

    const { uploadContext } = useContext(UploadContext);

    if (uploadContext.length) {
      panelNode = <UploadPanel key="upload" />;
    }

    const rowSelection: TableRowSelection<DbShare> = {
      selectedRowKeys: selectedKeys,
      onChange: (selectedRowKeys, selectedRows) => {
        // console.log('TenderSharesListTable onChange:', {
        //   selectedRowKeys,
        //   selectedRows,
        // });
      },
      onSelect: (record, selected) => {
        // console.log('TenderSharesListTable onSelect:', { record, selected });
        if (onSelect) {
          onSelect(
            dbTenderShareFileToTableEntry(record),
            selected,
            [],
            null as any
          );
        }
      },
      onSelectAll: (selected, selectedRows, changedRows) => {
        // console.log('TenderSharesListTable onSelectAll:', {
        //   selected,
        //   selectedRows,
        // });
        if (onSelectAll) {
          const entries = selectedRows
            .filter(Boolean)
            .map(dbTenderShareFileToTableEntry);
          const changed = changedRows
            .filter(Boolean)
            .map(dbTenderShareFileToTableEntry);
          onSelectAll(selected, entries, changed);
        }
      },
      getCheckboxProps: () => ({
        disabled: false,
      }),
    };

    const [logs, setLogs] = useState([]);

    const [dbGetShareLogs] = useMutation(getShareLogs, {
      onSuccess: (data) => {
        return data;
      },
      onError: (err) => {
        message.error(intl.formatMessage({ id: 'tender-share-update-error' }));
      },
    });

    // Memoized function to fetch logs data
    const getDetailsLogs = useCallback(async () => {
      if (projectId && tenderId && shareToDetailsLog) {
        try {
          const data = await dbGetShareLogs({
            projectId: Number(projectId),
            tenderId: Number(tenderId),
            shareKey: shareToDetailsLog.share_key_id,
          });
          return data;
        } catch (e) {
          console.error(e);
        }
      }
    }, [projectId, tenderId, shareToDetailsLog, dbGetShareLogs]);

    // Fetch logs when the modal becomes visible
    useEffect(() => {
      const fetchLogs = async () => {
        const data = await getDetailsLogs();
        setLogs(data || []);
      };

      if (shareToDetailsLog) {
        fetchLogs();
      }
    }, [shareToDetailsLog, getDetailsLogs]);

    // console.log(`selectedItems`, shareToEditInvite);

    // Primary-/Secondary Share Keys (vendor tab): the ellipsis-menu column
    // and right-click overlay are always mounted, mirroring the admin's
    // Shares tab exactly (same overlayItems/icons). onItemClick is unified
    // for both modes now - vendor rows only ever get dataContextMenu.
    // shareKey/shareKeyRemoved (never shareKeyClaimed/shareKeyRejected), so
    // handleRightOverlayItemClick can only ever reach delete/edit/
    // edit-invite/reactivate for a vendor row; claim/reject/details-log/
    // resend-invite stay unreachable exactly as they already are today.
    const rightClickWrapperProps = {
      items: overlayItems,
      disabled: !!selectedKeys?.length,
      onItemClick: handleRightOverlayItemClick,
      onOverlayClose: () =>
        setRightClickEntity({
          //
        }),
      focusOptions,
    };

    return (
      <div ref={ref}>
        <BaseRightClickOverlay {...rightClickWrapperProps}>
          <div ref={tableContainerRef}>
            <Table<DbShare>
              showSorterTooltip={false}
              css={[
                styles.table,
                tableProps.scroll?.y && {
                  '.ant-table-body': {
                    height: tableProps.scroll.y,
                  },
                },
              ]}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={intl.formatMessage({ id: 'no-data' })}
                  />
                ),
              }}
              className="main-content-window"
              pagination={false}
              bordered
              columns={columns}
              dataSource={shareKeysList}
              loading={isLoading}
              rowKey={(record) => record.share_key_id}
              rowSelection={viewOnly ? undefined : rowSelection}
              rowClassName={(record) => {
                if (
                  record.share_key_id === rightClickEntity.share?.share_key_id
                ) {
                  return 'ant-table-row-selected';
                }
                return '';
              }}
              onRow={(record) => ({
                onContextMenu: (e) => {
                  setRightClickEntity({ share: record });
                  handleContextMenuEvent(e);
                },
              })}
              expandable={
                // Primary-/Secondary Share Keys: only reserve the "+"
                // expand column at all when the feature is activated for
                // this tender - otherwise it's fully removed from the table.
                // Never on in vendor mode either - the Subcontractors tab
                // is already showing its own subcontractors list, no
                // nested drawer needed.
                isSecondaryKeysOn && !isOnMyTender
                  ? {
                      // Column 0 is the narrow ⋮ actions column (only when
                      // !viewOnly) - target the expand +/- icon at the status
                      // column instead, so it doesn't crowd it (same
                      // reasoning TenderMovementsTable uses an explicit
                      // expandIconColumnIndex).
                      expandIconColumnIndex: viewOnly ? 0 : 1,
                      // rc-table splices a brand-new column at
                      // expandIconColumnIndex (it doesn't share the status
                      // column's cell) - without an explicit width that
                      // column gets extra space under table-layout:auto,
                      // leaving the +/- button hugging the left edge
                      // instead of centered. Pin it to the same width as
                      // the other narrow columns (actions/status) and pair
                      // with the .ant-table-row-expand-icon-cell text-align
                      // rule in TenderSharesListTable.style.ts.
                      columnWidth: 40,
                      // Primary-/Secondary Share Keys (Tender Admin Update):
                      // hide the "+" entirely once we know (via the
                      // has_secondary_share_keys flag) there are no
                      // subcontractors to show - stays visible when the
                      // flag is true/undefined (backward compatible if the
                      // flag is ever missing on older/cached data).
                      rowExpandable: (record) =>
                        record.has_secondary_share_keys !== false,
                      // The "+" icon opens a side Drawer instead of
                      // expanding inline - expandedRowKeys stays
                      // permanently empty (controlled) so the Table itself
                      // never visually expands a row.
                      expandedRowKeys: [],
                      expandedRowRender: () => null,
                      onExpand: (_expanded, record) => {
                        // Defensive guard, belt-and-suspenders alongside
                        // rowExpandable above, in case antd ever lets the
                        // expand action through some other interaction.
                        if (record.has_secondary_share_keys === false) return;
                        setSecondaryDrawerRecord(record);
                      },
                    }
                  : undefined
              }
              {...tableProps}
            />
          </div>
        </BaseRightClickOverlay>
        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
          }}>
          {panelNode}
        </div>
        {/* Primary-/Secondary Share Keys: subcontractors of the clicked
            primary vendor. */}
        <SecondaryShareKeysDrawer
          parentShare={secondaryDrawerRecord}
          onClose={() => setSecondaryDrawerRecord(null)}
          secondaryShares={adminSecondaryShareKeysQuery.data ?? []}
          isLoading={adminSecondaryShareKeysQuery.isLoading}
        />

        {/* Primary-/Secondary Share Keys (vendor tab) - Invite (create)
            modal. The only real (POST) action for this pass; Remove/
            Reactivate are deferred to a later pass. */}
        {isOnMyTender && (
          <Modal
            title={intl.formatMessage({
              id: 'secondary-share-key-invite-modal-title',
            })}
            visible={isInviteModalOpen}
            onCancel={resetInviteForm}
            onOk={handleInviteSubmit}
            okButtonProps={{
              disabled: !isEmail(inviteEmail),
              loading: isInviting,
            }}
            okText={intl.formatMessage({ id: 'invite' })}
            cancelText={intl.formatMessage({ id: 'cancel' })}>
            <div css={styles.formField}>
              <div css={styles.fieldLabel}>
                {intl.formatMessage({ id: 'email' })}
              </div>
              <Input
                value={inviteEmail}
                disabled={isInviting}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={intl.formatMessage({ id: 'email' })}
              />
            </div>
            <div css={styles.formField}>
              <div css={styles.fieldLabel}>
                {intl.formatMessage({ id: 'company-name' })}
              </div>
              <Input
                value={inviteTitle}
                disabled={isInviting}
                onChange={(e) => setInviteTitle(e.target.value)}
                placeholder={intl.formatMessage({ id: 'company-name' })}
              />
            </div>
          </Modal>
        )}

        <SimpleConfirmModal
          modalTitle={`${intl.formatMessage({ id: 'reactivate' })} "${
            shareToReactivate?.share_key_title
          }"`}
          modalIcon={['far', 'repeat']}
          okText={intl.formatMessage({ id: 'reactivate' })}
          cancelText={intl.formatMessage({ id: 'cancel' })}
          onOk={handleReactivateShare}
          isLoading={isUpdateShareStateLoading}
          onCancel={() => setShareToReactivate(null)}
          visible={!!shareToReactivate}
        />
        {/* Automatic Resend Invite Modal*/}
        <SimpleConfirmModal
          modalTitle={`${intl.formatMessage({ id: 'resend-invite' })} "${
            shareToResendInvite?.share_key_title
          }"`}
          modalIcon={['far', 'envelope']}
          okText={intl.formatMessage({ id: 'resend-invite' })}
          cancelText={intl.formatMessage({ id: 'cancel' })}
          onOk={handleResendInvite}
          isLoading={isUpdateShareStateLoading}
          onCancel={() => setShareToResendInvite(null)}
          visible={!!shareToResendInvite}
        />
        {/* Manual Resend Invite Modal*/}
        {/*<EditInviteModal*/}
        {/*  modalTitle={`${intl.formatMessage({ id: 'edit-invite' })} "${*/}
        {/*    shareToEditInvite?.share_title*/}
        {/*  }"`}*/}
        {/*  projectId={projectId}*/}
        {/*  tenderId={Number(tenderId)}*/}
        {/*  shareKey={shareToEditInvite?.share_key_id}*/}
        {/*  visible={!!shareToEditInvite} // Turn ON/OFF modal | true/false*/}
        {/*  onCancel={() => setShareToEditInvite(null)}*/}
        {/*  status={shareToEditInvite?.share_key_state} // if 3 = can edit email, if else = cannot*/}
        {/*  previousData={{*/}
        {/*    name: shareToEditInvite?.share_key_title,*/}
        {/*    http: '',*/}
        {/*    phone: '',*/}
        {/*    email: shareToEditInvite?.share_key_email,*/}
        {/*    note: shareToEditInvite?.share_description,*/}
        {/*  }}*/}
        {/*  emailDisabled={!!editableShare?.user_id}*/}
        {/*/>*/}
        <InviteModal
          tenderId={tenderId}
          initialShares={shareToEditInvite}
          mode={'edit-resend'}
          visible={!!shareToEditInvite}
          onCancel={() => setShareToEditInvite(null)}
        />

        {/* Details(Log) Modal*/}
        <DetailsLogModal
          projectId={projectId}
          tenderId={Number(tenderId)}
          shareKey={shareToDetailsLog?.share_key_id}
          status={shareToDetailsLog?.share_key_state}
          detailLog={logs}
          previousData={shareToDetailsLog}
          visible={!!shareToDetailsLog}
          onCancel={() => setShareToDetailLog(null)}
        />
        {/* reject */}
        <SimpleDeleteModal
          modalTitle={`${intl.formatMessage({ id: 'reject' })} "${
            shareToReject?.share_key_title
          }"`}
          modalIcon={['far', 'circle-xmark']}
          okText={intl.formatMessage({ id: 'reject' })}
          cancelText={intl.formatMessage({ id: 'cancel' })}
          onOk={handleRejectShare}
          isLoading={isUpdateShareStateLoading}
          onCancel={() => setShareToReject(null)}
          visible={!!shareToReject}
        />
        {/* claim */}
        <SimpleDeleteModal
          modalTitle={`${intl.formatMessage({ id: 'claim' })} "${
            shareToClaim?.share_key_title
          }"`}
          modalIcon={['far', 'circle-plus']}
          okText={intl.formatMessage({ id: 'claim' })}
          cancelText={intl.formatMessage({ id: 'cancel' })}
          onOk={handleClaimShare}
          isLoading={isUpdateShareStateLoading}
          onCancel={() => setShareToClaim(null)}
          visible={!!shareToClaim}
        />
        {/* Delete share */}
        <SimpleDeleteModal
          modalTitle={`${intl.formatMessage({ id: 'share-delete' })} "${
            shareToRemove?.share_key_title
          }"`}
          modalIcon={['far', 'trash']}
          okText={intl.formatMessage({ id: 'delete' })}
          cancelText={intl.formatMessage({ id: 'cancel' })}
          onOk={handleRemoveShare}
          customText={intl.formatMessage({ id: 'delete-share-warning' })}
          isLoading={isUpdateShareStateLoading}
          onCancel={() => setShareToRemove(null)}
          visible={!!shareToRemove}
        />
        {/* Edit Share: Company Name/Account || Email; turned off from context menu */}

        {!isOnMyTender && projectId && tenderId && shareToEdit && (
          <EditShareTenderModal
            projectId={projectId}
            tenderId={Number(tenderId)}
            shareKey={shareToEdit?.share_key_id}
            visible={!!shareToEdit}
            onCancel={() => setShareToEdit(null)}
            previousData={{
              email: shareToEdit?.share_key_email,
              title: shareToEdit?.share_key_title,
            }}
            emailDisabled={!!editableShare?.user_id}
          />
        )}

        {/* Primary-/Secondary Share Keys (vendor tab): Edit modal - title +
            email, email locked once the secondary share key is claimed. */}
        {isOnMyTender && shareId && shareKey && (
          <EditSecondaryShareKeyModal
            shareId={shareId}
            shareKey={shareKey}
            secondaryShareKey={shareToEdit?.share_key_id ?? ''}
            emailDisabled={!!shareToEdit?.share_key_is_claimed}
            previousData={{
              email: shareToEdit?.share_key_email,
              title: shareToEdit?.share_key_title,
            }}
            visible={!!shareToEdit}
            onCancel={() => setShareToEdit(null)}
          />
        )}
      </div>
    );
  }
);
